/* SocialQrModal — text in English and German (same shape in both).
   `networks` holds the title / alt / hint / button text for each social
   network, so callers only need to pass the link. */
import { ByLang, useL } from "i18n/lang";

export type SocialNetwork = "whatsapp" | "instagram" | "youtube" | "facebook";

interface NetworkText { title: string; qrAlt: string; scanHint: string; buttonLabel: string; }

const en = {
  or: "or",
  closeAria: "Close",
  networks: {
    whatsapp: {
      title: "Join our WhatsApp Community",
      qrAlt: "WhatsApp Community QR code",
      scanHint: "Scan with your phone's camera or WhatsApp",
      buttonLabel: "Open WhatsApp Web",
    },
    instagram: {
      title: "Follow us on Instagram",
      qrAlt: "Instagram profile QR code",
      scanHint: "Scan with your phone's camera or Instagram",
      buttonLabel: "Open Instagram",
    },
    youtube: {
      title: "Subscribe on YouTube",
      qrAlt: "YouTube channel QR code",
      scanHint: "Scan with your phone's camera",
      buttonLabel: "Open YouTube",
    },
    facebook: {
      title: "Follow us on Facebook",
      qrAlt: "Facebook page QR code",
      scanHint: "Scan with your phone's camera",
      buttonLabel: "Open Facebook",
    },
  } as Record<SocialNetwork, NetworkText>,
};

export type SocialQrText = typeof en;

const de: SocialQrText = {
  or: "oder",
  closeAria: "Schließen",
  networks: {
    whatsapp: {
      title: "Tritt unserer WhatsApp-Community bei",
      qrAlt: "QR-Code der WhatsApp-Community",
      scanHint: "Scanne den Code mit der Handykamera oder mit WhatsApp",
      buttonLabel: "WhatsApp Web öffnen",
    },
    instagram: {
      title: "Folge uns auf Instagram",
      qrAlt: "QR-Code des Instagram-Profils",
      scanHint: "Scanne den Code mit der Handykamera oder mit Instagram",
      buttonLabel: "Instagram öffnen",
    },
    youtube: {
      title: "Abonniere uns auf YouTube",
      qrAlt: "QR-Code des YouTube-Kanals",
      scanHint: "Scanne den Code mit der Handykamera",
      buttonLabel: "YouTube öffnen",
    },
    facebook: {
      title: "Folge uns auf Facebook",
      qrAlt: "QR-Code der Facebook-Seite",
      scanHint: "Scanne den Code mit der Handykamera",
      buttonLabel: "Facebook öffnen",
    },
  },
};

export const socialQrText: ByLang<SocialQrText> = { en, de };
export const useSocialQrText = () => useL(socialQrText);

/** Guess the network from the link, so existing callers get German text too. */
export function networkFromLink(link: string): SocialNetwork | undefined {
  if (/whatsapp\.com/i.test(link)) return "whatsapp";
  if (/instagram\.com/i.test(link)) return "instagram";
  if (/youtube\.com|youtu\.be/i.test(link)) return "youtube";
  if (/facebook\.com/i.test(link)) return "facebook";
  return undefined;
}
